import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module registry. Future modules plug in here with a `to` route.
const MODULES = [
  { key: "enquiries", label: "Enquiries", to: "/hq" },
  { key: "trends", label: "Trend Room", to: "/hq/trends" },
  { label: "Business Development" },
  { label: "Producer Calendar" },
  { label: "Finance & Profitability" },
  { label: "Contracts & Agreements" },
  { label: "Invoices & Payments" },
  { label: "Production" },
  { label: "Team & Crew" },
  { label: "Client CRM" },
  { label: "Creative Lab" },
  { label: "Marketing Intelligence" },
  { label: "Growth Intelligence" },
  { label: "Ida\u2019s Memory" },
];

const HQContext = createContext(null);
export const useHQ = () => useContext(HQContext);

export default function HQLayout({ children }) {
  const { user, loading: authLoading, signIn } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null);
  const [radar, setRadar] = useState({ total: 0, urgent: 0 });
  const location = useLocation();
  const hqValue = useMemo(() => ({ user }), [user]);

  // Radar badge: urgency visible from anywhere in HQ, refreshed quietly.
  useEffect(() => {
    if (!isAdmin) return;
    const load = () =>
      axios
        .get(`${API}/hq/radar`)
        .then(({ data }) => {
          const signals = data.signals || [];
          setRadar({ total: signals.length, urgent: signals.filter((s) => s.severity === "high").length });
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [isAdmin]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/journal/admin/whoami`);
        setIsAdmin(!!data.is_admin);
      } catch {
        setIsAdmin(false);
      }
    })();
  }, [user, authLoading]);

  if (authLoading || (user && isAdmin === null)) {
    return (
      <div className="min-h-screen bg-[#0c111c] flex items-center justify-center">
        <div className="text-white/40 text-sm tracking-[0.1em] uppercase">Opening HQ…</div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#0c111c] flex items-center justify-center px-6">
        <div className="max-w-[420px] text-center" data-testid="hq-gate">
          <Lock className="w-5 h-5 text-white/30 mx-auto" />
          <p className="mt-6 font-display font-extrabold text-white text-[28px] tracking-[-0.02em]">
            BRAND NEW MONEY <span className="font-serif-italic font-light">HQ</span>
          </p>
          <p className="mt-4 text-white/50 text-[14.5px] leading-[1.7]">
            {user
              ? "This is the private operating system of Brand New Money Films. Your account doesn\u2019t have access."
              : "Sign in with the studio account to enter."}
          </p>
          {!user && (
            <button
              type="button"
              onClick={signIn}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0c111c] text-[14px] font-medium hover:bg-white/90 transition-colors"
              data-testid="hq-signin"
            >
              Sign in <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <div className="mt-10">
            <Link to="/" className="text-white/40 text-[13px] hover:text-white/70 transition-colors">
              Back to the site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <HQContext.Provider value={hqValue}>
      <div className="min-h-screen bg-[#0c111c] text-white flex" data-testid="hq-shell">
        {/* Side rail */}
        <aside className="hidden lg:flex flex-col w-[248px] shrink-0 border-r border-white/[0.07] px-6 py-8 sticky top-0 h-screen overflow-y-auto">
          <Link to="/" className="block">
            <div className="font-display font-extrabold text-[15px] tracking-[0.01em] leading-tight">
              BRAND NEW MONEY
            </div>
            <div className="font-serif-italic font-light text-[13px] text-white/60">
              Films <span className="text-[#E53935]">&#9679;</span> <span className="not-italic font-display text-white/40 tracking-[0.14em] text-[10px] uppercase ml-1">HQ</span>
            </div>
          </Link>
          <nav className="mt-10 flex-1 space-y-0.5">
            {MODULES.map((m) => {
              const active = m.to && (location.pathname === m.to || (m.to === "/hq" && location.pathname.startsWith("/hq")));
              if (m.to) {
                return (
                  <Link
                    key={m.label}
                    to={m.to}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg text-[13.5px] transition-colors ${
                      active ? "bg-white/[0.08] text-white" : "text-white/60 hover:text-white hover:bg-white/[0.04]"
                    }`}
                    data-testid={`hq-module-${m.key}`}
                  >
                    {m.label}
                    {m.key === "enquiries" && radar.total > 0 && (
                      <span
                        className={`ml-2 min-w-[20px] h-[20px] px-1.5 inline-flex items-center justify-center rounded-full text-[10.5px] font-semibold ${
                          radar.urgent > 0 ? "bg-[#E53935] text-white" : "bg-amber-300/15 text-amber-200 border border-amber-300/25"
                        }`}
                        data-testid="hq-radar-badge"
                        title={`${radar.total} radar signal${radar.total > 1 ? "s" : ""}${radar.urgent ? `, ${radar.urgent} urgent` : ""}`}
                      >
                        {radar.total}
                      </span>
                    )}
                  </Link>
                );
              }
              return (
                <div
                  key={m.label}
                  className="flex items-center justify-between px-3 py-2 rounded-lg text-[13.5px] text-white/25 cursor-default select-none"
                >
                  {m.label}
                  <span className="text-[9px] uppercase tracking-[0.1em] text-white/20 border border-white/10 rounded-full px-1.5 py-0.5">Soon</span>
                </div>
              );
            })}
          </nav>
          <div className="pt-6 border-t border-white/[0.07] text-[11.5px] text-white/30 leading-[1.6]">
            The private operating system of Brand New Money Films.
          </div>
        </aside>

        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </HQContext.Provider>
  );
}
